import { Component } from "react";
import Helmet from "react-helmet";
import StateConstants from "src/states/StateContstants";
import HouseInformationHelper from "src/utils/HouseInformationHelper";

interface ValuationPageSEOProps {
    propertyId: string;
    valuation: any;
    stateHandler?: any;
}

interface ValuationPageSEOState {}

class ValuationPageSEO extends Component<ValuationPageSEOProps, ValuationPageSEOState> {
    private readonly BASE_URL = "https://homeindex.ie";
    private readonly INCLUDE_IMAGE_IN_SEO_TIMESTAMP_IN_SECONDS = (new Date(2026, 1, 1)).getTime() / 1000;
    private readonly ENABLE_IMAGE_IN_SEO = false;

    formatCurrencyForSEO = (amount: number): string => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            useGrouping: true // Commas between thousands
        }).format(amount);
    }

    formatCurrencyForTitle = (amount: number): string => {
        if (amount >= 1000) {
            const thousands = Math.round(amount / 1000);
            return `€${thousands}K`;
        }
        return `€${amount}`;
    }

    buildOrganizationStructuredData = (country: string) => {
        return {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "HomeIndex",
            "url": this.BASE_URL,
            "description": "Independent property valuation service",
            "address": {
                "@type": "PostalAddress",
                "addressCountry": country
            }
        };
    }

    buildPropertyStructuredData = (
        valuation: any,
        description: string,
        canonicalUrl: string
    ) => {
        if (!valuation) {
            return {
                "@context": "https://schema.org",
                "@type": "Place",
                "name": "",
                "description": description
            };
        }

        const address = valuation["a"] || "";
        const isStreetAddress = valuation["isa"] || false;
        const currentValuation = valuation["valuation"] || 0;
        const country = valuation["c"] || "Ireland";
        const streetName = valuation["s"] || "";
        const neighbourhood = valuation["n"] || "";
        const region = valuation["r"] || "";
        const postalCode = valuation["pc"] || "";
        const coordinates = valuation["gc"];
        const addressFeatures = valuation["af"];

        const propertyStructuredData: any = {
            "@context": "https://schema.org",
            "@type": "RealEstateListing",
            "name": address,
            "description": description,
            "url": canonicalUrl,
            "address": {
                "@type": "PostalAddress",
                "addressCountry": country
            }
        };

        // Add detailed address components
        if (isStreetAddress && streetName && streetName.length > 0) {
            propertyStructuredData.address.streetAddress = streetName;
        }
        if (neighbourhood && neighbourhood.length > 0) {
            propertyStructuredData.address.addressLocality = neighbourhood;
        }
        if (region && region.length > 0) {
            propertyStructuredData.address.addressRegion = region;
        }
        if (postalCode && postalCode.length > 0) {
            propertyStructuredData.address.postalCode = postalCode;
        }

        // Add GeoCoordinates
        if (coordinates && typeof coordinates === "object" && typeof coordinates.lt === "string" && typeof coordinates.ln === "string") {
            propertyStructuredData.geo = {
                "@type": "GeoCoordinates",
                "latitude": parseFloat(coordinates.lt),
                "longitude": parseFloat(coordinates.ln)
            };
        }

        // Add price range
        if (currentValuation > 0) {
            propertyStructuredData.priceRange = `${this.formatCurrencyForSEO(valuation["valuation_lower"] || currentValuation)}–${this.formatCurrencyForSEO(valuation["valuation_upper"] || currentValuation)}`;
            propertyStructuredData.priceCurrency = "EUR";
        }

        // Add property features
        if (addressFeatures && typeof addressFeatures === "object") {
            if (typeof addressFeatures.bd === "number") {
                propertyStructuredData.numberOfBedrooms = addressFeatures.bd;
            }
            if (typeof addressFeatures.bt === "number") {
                propertyStructuredData.numberOfBathroomsTotal = addressFeatures.bt;
            }
            if (typeof addressFeatures.fa === "number") {
                propertyStructuredData.floorSize = {
                    "@type": "QuantitativeValue",
                    "value": addressFeatures.fa,
                    "unitCode": "MTK"
                };
            }
        }

        return propertyStructuredData;
    }

    buildImageStructuredData = (imageUrl: string, address: string) => {
        return {
            "@context": "https://schema.org",
            "@type": "ImageObject",
            "contentUrl": imageUrl,
            "name": `Street view of ${address}`,
            "description": `Street view image of ${address}`
        };
    }

    buildTitle = (addressShortened: string, formattedLower: string, formattedUpper: string): string => {
        return `${formattedLower}–${formattedUpper} · ${addressShortened} · Valuation`;
    }

    buildDescription = (valuation: any, address: string, formattedLower: string, formattedUpper: string, country: string, nearbyHomesCount: number): string => {
        const parts = [
            "Independent Valuation",
            "Estimated range " + formattedLower + "–" + formattedUpper,
            address,
            this.buildAvailableDataDescription(valuation, nearbyHomesCount)
        ];
        
        const salesHistory = this.buildSalesHistoryDescription(valuation);
        if (salesHistory.length > 0) {
            parts.push(salesHistory);
        }
        
        parts.push("HomeIndex Ireland's Instant Property Valuation Service");
        
        return parts.join(" · ");
    }

    buildAvailableDataDescription = (valuation: any, nearbyHomesCount: number): string => {
        let availableDataDescription = "Available data";
        let availableDataDescriptionContent = [];
        if (nearbyHomesCount > 0) {
            availableDataDescriptionContent.push(`${nearbyHomesCount} local sales`);
        }
        if (valuation["af"]) {
            availableDataDescriptionContent.push(`${valuation["af"]["bd"]} bedrooms, ${valuation["af"]["bt"]} bathrooms, ${valuation["af"]["fa"]} sqm`);
        }
        const timeSeries = valuation["vts"] || valuation["time_series"];
        if (timeSeries) {
            const oneMonthChange = HouseInformationHelper.calculatePriceChange(timeSeries, HouseInformationHelper.getOneMonthAgoDate());
            if (oneMonthChange) {
                const changeDirection = oneMonthChange.isPositive ? "up" : "down";
                availableDataDescriptionContent.push(`value ${changeDirection} ${this.formatCurrencyForSEO(oneMonthChange.change)} this month`);
            }
        }
        return `${availableDataDescription} ${availableDataDescriptionContent.join(", ")}`;
    }

    buildSalesHistoryDescription = (valuation: any): string => {
        const salesHistory = valuation["sh"];

        let sortedSalesKeys = Object.keys(salesHistory).sort();

        if (sortedSalesKeys.length === 0) {
            return "";
        }

        const latestSale = salesHistory[sortedSalesKeys[sortedSalesKeys.length - 1]];
        
        const historyContent: string[] = [];

        if (latestSale && latestSale.p && latestSale.d) {
            const salePrice = this.formatCurrencyForSEO(latestSale.p.v);
            historyContent.push("Last sold for " + salePrice + " on " + latestSale.d);
        }

        if (sortedSalesKeys.length > 1) {
            historyContent.push(sortedSalesKeys.length + " recorded sales");
        }

        return historyContent.join(", ");
    }

    buildCanonicalUrl = (propertyId: string, addressSlug: string): string => {
        return `${this.BASE_URL}/valuate/${encodeURIComponent(propertyId)}/${encodeURIComponent(addressSlug)}`;
    }

    buildImageUrl = (propertyId: string): string => {
        // Use property title image endpoint (GET by id); image is 1200x675 (16:9)
        return `https://apis.homeindex.ie/get_property_title_image?id=${encodeURIComponent(propertyId)}`;
    }

    buildKeywords = (address: string, country: string, formattedValuation: string): string => {
        return `house valuation, property valuation, ${address}, ${country}, real estate, property value, home valuation, ${formattedValuation}`;
    }

    buildEnhancedKeywords = (
        address: string, 
        neighbourhood: string, 
        region: string, 
        country: string, 
        formattedValuation: string
    ): string => {
        const keywords = [
            "house valuation",
            "property valuation",
            address,
            country,
            "real estate",
            "property value",
            "home valuation",
            formattedValuation
        ];

        if (neighbourhood && neighbourhood.length > 0) {
            keywords.push(neighbourhood + " property prices");
            keywords.push(neighbourhood + " house values");
            keywords.push("property valuation " + neighbourhood);
        }
        if (region && region.length > 0 && region !== neighbourhood) {
            keywords.push(region + " property");
            keywords.push(region + " real estate");
        }

        return keywords.filter(k => k && k.length > 0).join(", ");
    }

    render() {
        const { propertyId, valuation, stateHandler } = this.props;

        if (!valuation) {
            return <div></div>;
        }

        const address = valuation["a"] || "";
        const addressShortened = valuation["da"] || "";
        const addressSlug = valuation["aurl"];
        const isStreetAddress = valuation["isa"] || false;
        const currentValuation = valuation["valuation"] || 0;
        const roundToNearest = (value: number, nearest: number) => Math.round(value / nearest) * nearest;
        const valuationLower = roundToNearest(valuation["valuation_lower"] || currentValuation, 50000);
        const valuationUpper = roundToNearest(valuation["valuation_upper"] || currentValuation, 50000);
        const formattedLowerForTitle = this.formatCurrencyForTitle(valuationLower);
        const formattedUpperForTitle = this.formatCurrencyForTitle(valuationUpper);
        const formattedLowerForDescription = this.formatCurrencyForSEO(valuationLower);
        const formattedUpperForDescription = this.formatCurrencyForSEO(valuationUpper);
        const country = valuation["c"] || "Ireland";
        const neighbourhood = valuation["n"] || "";
        const region = valuation["r"] || "";
        const coordinates = valuation["gc"];

        // Check if page has nearby homes (comparable addresses)
        let hasNearbyHomes = false;
        let nearbyHomesCount = 0;
        if (stateHandler) {
            const comparableHomes = stateHandler.get(StateConstants.COMPARABLE_HOMES_STATE);
            const comparableAddresses = comparableHomes?.["comparable_addresses"] || [];
            hasNearbyHomes = comparableAddresses.length > 0;
            nearbyHomesCount = comparableAddresses.length;
        }

        // Also check for error state
        let hasError = false;
        if (stateHandler) {
            const error = stateHandler.get("error");
            if (error && error.message) {
                const errorMessage = error.message.toLowerCase();
                hasError = errorMessage.includes("no nearby sold homes were found");
            }
        }

        // Determine if page should be indexed
        const shouldIndex = hasNearbyHomes && !hasError;

        const title = this.buildTitle(addressShortened, formattedLowerForTitle, formattedUpperForTitle);
        const description = this.buildDescription(valuation, address, formattedLowerForDescription, formattedUpperForDescription, country, nearbyHomesCount);
        const canonicalUrl = this.buildCanonicalUrl(propertyId, addressSlug);
        const imageUrl = this.buildImageUrl(propertyId);
        const keywords = this.buildEnhancedKeywords(address, neighbourhood, region, country, formattedLowerForDescription);
        const geoRegion = country === "Ireland" ? "IE" : country;

        const organizationStructuredData = this.buildOrganizationStructuredData(country);
        const propertyStructuredData = this.buildPropertyStructuredData(
            valuation,
            description,
            canonicalUrl
        );
        const imageStructuredData = this.buildImageStructuredData(imageUrl, address);
        const creationTimestamp = valuation["cts"] || 0;

        const shouldIncludeImageInSEO = creationTimestamp > this.INCLUDE_IMAGE_IN_SEO_TIMESTAMP_IN_SECONDS && this.ENABLE_IMAGE_IN_SEO;


        return (
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description}/>
                <meta name="keywords" content={keywords}/>
                <link rel="canonical" href={canonicalUrl}/>
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website"/>
                <meta property="og:url" content={canonicalUrl}/>
                <meta property="og:title" content={title}/>
                <meta property="og:description" content={description}/>
                {shouldIncludeImageInSEO && (
                    <>
                        <meta property="og:image" content={imageUrl}/>
                        <meta property="og:image:width" content="1200"/>
                        <meta property="og:image:height" content="675"/>
                        <meta property="og:image:alt" content={`Street view of ${address}`}/>
                    </>
                )}
                <meta property="og:site_name" content="HomeIndex"/>
                <meta property="og:locale" content="en_IE"/>
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image"/>
                <meta name="twitter:title" content={title}/>
                <meta name="twitter:description" content={description}/>
                {shouldIncludeImageInSEO && (
                    <>
                        <meta name="twitter:image" content={imageUrl}/>
                        <meta name="twitter:image:alt" content={`Street view of ${address}`}/>
                    </>
                )}
                <meta name="twitter:site" content="@homeindex"/>
                
                {/* Additional SEO meta tags */}
                <meta name="robots" content={shouldIndex ? "index, follow" : "noindex, nofollow"}/>
                <meta name="author" content="HomeIndex"/>
                <meta name="geo.region" content={geoRegion}/>
                <meta name="geo.placename" content={address}/>
                {coordinates && typeof coordinates.lt === "string" && typeof coordinates.ln === "string" ? (
                    <meta name="geo.position" content={coordinates.lt + ";" + coordinates.ln}/>
                ) : null}
                {coordinates && typeof coordinates.lt === "string" && typeof coordinates.ln === "string" ? (
                    <meta name="ICBM" content={coordinates.lt + ", " + coordinates.ln}/>
                ) : null}
                {neighbourhood.length > 0 ? (
                    <meta name="geo.locality" content={neighbourhood}/>
                ) : null}
                
                {/* Structured Data */}
                <script 
                    type="application/ld+json" 
                    dangerouslySetInnerHTML={{__html: JSON.stringify(propertyStructuredData)}}
                />
                <script 
                    type="application/ld+json" 
                    dangerouslySetInnerHTML={{__html: JSON.stringify(organizationStructuredData)}}
                />
                {shouldIncludeImageInSEO && (
                    <script 
                        type="application/ld+json" 
                        dangerouslySetInnerHTML={{__html: JSON.stringify(imageStructuredData)}}
                    />
                )}
            </Helmet>
        );
    }
}

export default ValuationPageSEO;

